/**
 * AgentConfigSettings
 * Code execution prompts, quality gate commands, verify checks, required credentials.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Save, RotateCcw, Check, RotateCw } from 'lucide-react';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import { StringListEditor } from '../build-config/StringListEditor';
import { useAgentConfig, useUpdateAgentConfig, type AgentConfig, type AgentConfigUpdate } from '../../api/workspace-agent-config';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// ─── Textarea Field ─────────────────────────────────────────────

function PromptField({
  label,
  description,
  value,
  defaultValue,
  onChange,
  onReset,
  rows,
}: {
  label: string;
  description?: string;
  value: string;
  defaultValue: string;
  onChange: (v: string) => void;
  onReset: () => void;
  rows?: number;
}) {
  const isCustomized = value !== defaultValue;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-[12px] font-medium text-text">{label}</span>
          {description && (
            <p className="text-[11px] text-subtle">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
            isCustomized ? 'bg-coral-50 text-coral-600' : 'bg-surface-subtle text-subtle'
          }`}>
            {isCustomized ? 'customized' : 'default'}
          </span>
          {isCustomized && (
            <button
              onClick={onReset}
              className="text-[11px] text-subtle hover:text-muted flex items-center gap-0.5"
              title="Reset to default"
            >
              <RotateCw size={10} />
              Reset
            </button>
          )}
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows ?? 4}
        className="w-full px-3 py-2 border border-line rounded-lg text-text bg-white font-mono text-[12px] leading-relaxed focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none resize-y"
      />
    </div>
  );
}

// ─── Key-Value Editor ───────────────────────────────────────────

function KeyValueEditor({
  value,
  onChange,
}: {
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  const entries = Object.entries(value);

  const handleChange = (oldKey: string, newKey: string, newValue: string) => {
    const updated = { ...value };
    if (oldKey !== newKey) delete updated[oldKey];
    updated[newKey] = newValue;
    onChange(updated);
  };

  const handleRemove = (key: string) => {
    const updated = { ...value };
    delete updated[key];
    onChange(updated);
  };

  const handleAdd = () => {
    onChange({ ...value, '': '' });
  };

  return (
    <div className="space-y-2">
      {entries.map(([key, val], i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={key}
            onChange={(e) => handleChange(key, e.target.value, val)}
            placeholder="key"
            className="w-1/3 px-2 py-1.5 border border-line rounded text-[12px] font-mono focus:ring-1 focus:ring-coral-400 outline-none"
          />
          <input
            type="text"
            value={val}
            onChange={(e) => handleChange(key, key, e.target.value)}
            placeholder="command"
            className="flex-1 px-2 py-1.5 border border-line rounded text-[12px] font-mono focus:ring-1 focus:ring-coral-400 outline-none"
          />
          <button
            onClick={() => handleRemove(key)}
            className="p-1 text-subtle hover:text-red-500 transition-colors"
          >
            &times;
          </button>
        </div>
      ))}
      <button
        onClick={handleAdd}
        className="text-[12px] text-coral-600 hover:text-coral-700 font-medium"
      >
        + Add gate
      </button>
    </div>
  );
}

// ─── Checkbox Group ─────────────────────────────────────────────

const ALL_CHECKS = ['lint', 'typecheck', 'test', 'build'];

function CheckboxGroup({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {ALL_CHECKS.map(check => (
        <label key={check} className="flex items-center gap-1.5 text-[12px] text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={value.includes(check)}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...value, check]);
              } else {
                onChange(value.filter(v => v !== check));
              }
            }}
            className="rounded border-line-strong text-coral-500 focus:ring-coral-400"
          />
          {check}
        </label>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

interface AgentConfigSettingsProps {
  sectionRefs?: Record<string, React.RefObject<HTMLDivElement>>;
}

export const AgentConfigSettings: React.FC<AgentConfigSettingsProps> = ({ sectionRefs }) => {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const { data: config, isLoading } = useAgentConfig(workspaceId);
  const updateMutation = useUpdateAgentConfig();

  const [changes, setChanges] = useState<AgentConfigUpdate>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    setChanges({});
  }, [config]);

  const getCodeExec = useCallback((key: keyof NonNullable<AgentConfig['codeExecution']>) => {
    return changes.codeExecution?.[key] ?? config?.codeExecution?.[key] ?? '';
  }, [changes, config]);

  const getDefaults = useCallback(<K extends keyof NonNullable<AgentConfig['defaults']>>(key: K) => {
    return changes.defaults?.[key] ?? config?.defaults?.[key];
  }, [changes, config]);

  const setCodeExec = useCallback((key: string, value: string) => {
    setChanges(prev => ({
      ...prev,
      codeExecution: { ...prev.codeExecution, [key]: value },
    }));
    setSaveStatus('idle');
  }, []);

  const resetCodeExec = useCallback((key: string) => {
    // Setting to empty string means "use default" — the server will merge defaults
    setChanges(prev => ({
      ...prev,
      codeExecution: { ...prev.codeExecution, [key]: '' },
    }));
    setSaveStatus('idle');
  }, []);

  const setDefaults = useCallback((key: string, value: any) => {
    setChanges(prev => ({
      ...prev,
      defaults: { ...prev.defaults, [key]: value },
    }));
    setSaveStatus('idle');
  }, []);

  const getSpriteInstructions = useCallback(<K extends keyof NonNullable<AgentConfig['spriteInstructions']>>(key: K) => {
    return changes.spriteInstructions?.[key] ?? config?.spriteInstructions?.[key];
  }, [changes, config]);

  const setSpriteInstructions = useCallback((key: string, value: any) => {
    setChanges(prev => ({
      ...prev,
      spriteInstructions: { ...prev.spriteInstructions, [key]: value },
    }));
    setSaveStatus('idle');
  }, []);

  const resetSpriteInstructions = useCallback((key: string) => {
    setChanges(prev => ({
      ...prev,
      spriteInstructions: { ...prev.spriteInstructions, [key]: '' },
    }));
    setSaveStatus('idle');
  }, []);

  const hasChanges = Object.keys(changes.codeExecution || {}).length > 0
    || Object.keys(changes.defaults || {}).length > 0
    || Object.keys(changes.spriteInstructions || {}).length > 0;

  const handleSave = useCallback(async () => {
    if (!workspaceId || !hasChanges) return;
    setSaveStatus('saving');
    try {
      await updateMutation.mutateAsync({ workspaceId, config: changes });
      setChanges({});
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }, [workspaceId, hasChanges, changes, updateMutation]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => <div key={i} className="h-48 bg-surface-subtle rounded-lg animate-pulse" />)}
      </div>
    );
  }

  // Default values for comparison (empty string = use server default)
  const defaultCodeExec = config?.codeExecution || {
    systemPromptPersona: '', projectContext: '', developmentStandards: '',
    executionInstructions: '', gitBranchingTemplate: '',
  };

  return (
    <div className="space-y-6">
      {/* Code Execution */}
      <div ref={sectionRefs?.['Code Execution']}>
        <Panel>
          <PanelHeader title="Code Execution Prompts" />
          <PanelBody>
            <div className="space-y-5">
              <PromptField
                label="System Persona"
                description="Base identity and behavioral instructions injected into every agent system prompt"
                value={getCodeExec('systemPromptPersona')}
                defaultValue={defaultCodeExec.systemPromptPersona}
                onChange={(v) => setCodeExec('systemPromptPersona', v)}
                onReset={() => resetCodeExec('systemPromptPersona')}
                rows={3}
              />
              <PromptField
                label="Project Context"
                description="Repository structure, tech stack, and architectural context provided to agents"
                value={getCodeExec('projectContext')}
                defaultValue={defaultCodeExec.projectContext}
                onChange={(v) => setCodeExec('projectContext', v)}
                onReset={() => resetCodeExec('projectContext')}
                rows={5}
              />
              <PromptField
                label="Development Standards"
                description="Coding conventions, linting rules, and quality expectations agents must follow"
                value={getCodeExec('developmentStandards')}
                defaultValue={defaultCodeExec.developmentStandards}
                onChange={(v) => setCodeExec('developmentStandards', v)}
                onReset={() => resetCodeExec('developmentStandards')}
                rows={4}
              />
              <PromptField
                label="Execution Instructions"
                description="Step-by-step workflow agents follow when implementing code changes"
                value={getCodeExec('executionInstructions')}
                defaultValue={defaultCodeExec.executionInstructions}
                onChange={(v) => setCodeExec('executionInstructions', v)}
                onReset={() => resetCodeExec('executionInstructions')}
                rows={5}
              />
              <PromptField
                label="Git Branching Template"
                description="Branch naming pattern and commit message conventions for agent-created branches"
                value={getCodeExec('gitBranchingTemplate')}
                defaultValue={defaultCodeExec.gitBranchingTemplate}
                onChange={(v) => setCodeExec('gitBranchingTemplate', v)}
                onReset={() => resetCodeExec('gitBranchingTemplate')}
                rows={5}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Defaults */}
      <div ref={sectionRefs?.['Defaults']}>
        <Panel>
          <PanelHeader title="Agent Defaults" />
          <PanelBody>
            <div className="space-y-5">
              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block">
                  Required Credentials
                </label>
                <p className="text-[11px] text-subtle mb-2">API keys and tokens agents must have access to before executing tasks</p>
                <StringListEditor
                  items={getDefaults('requiredCredentials') as string[] ?? []}
                  onChange={(v) => setDefaults('requiredCredentials', v)}
                  placeholder="e.g. ANTHROPIC_API_KEY"
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block">
                  Quality Gate Commands
                </label>
                <p className="text-[11px] text-subtle mb-2">Named shell commands run as quality gates after each agent code change</p>
                <KeyValueEditor
                  value={getDefaults('qualityGateCommands') as Record<string, string> ?? {}}
                  onChange={(v) => setDefaults('qualityGateCommands', v)}
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block">
                  Default Verify Checks
                </label>
                <p className="text-[11px] text-subtle mb-2">Automated checks enabled by default for agent verification steps</p>
                <CheckboxGroup
                  value={getDefaults('defaultVerifyChecks') as string[] ?? []}
                  onChange={(v) => setDefaults('defaultVerifyChecks', v)}
                />
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Sprite Instructions */}
      <div ref={sectionRefs?.['Sprite Instructions']}>
        <Panel>
          <PanelHeader title="Sprite Instructions" />
          <PanelBody>
            <div className="space-y-5">
              <PromptField
                label="CLAUDE.md Template"
                description="Default CLAUDE.md content injected into every sprite agent's workspace"
                value={(getSpriteInstructions('claudeMdTemplate') as string) ?? ''}
                defaultValue={config?.spriteInstructions?.claudeMdTemplate ?? ''}
                onChange={(v) => setSpriteInstructions('claudeMdTemplate', v)}
                onReset={() => resetSpriteInstructions('claudeMdTemplate')}
                rows={10}
              />
              <PromptField
                label="Additional Instructions"
                description="Extra context or constraints appended to sprite system prompts"
                value={(getSpriteInstructions('additionalInstructions') as string) ?? ''}
                defaultValue={config?.spriteInstructions?.additionalInstructions ?? ''}
                onChange={(v) => setSpriteInstructions('additionalInstructions', v)}
                onReset={() => resetSpriteInstructions('additionalInstructions')}
                rows={4}
              />
              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block">
                  Code Conventions
                </label>
                <p className="text-[11px] text-subtle mb-2">Coding patterns and style rules enforced across all sprite agents</p>
                <StringListEditor
                  items={(getSpriteInstructions('codeConventions') as string[]) ?? []}
                  onChange={(v) => setSpriteInstructions('codeConventions', v)}
                  placeholder="e.g. Use async/await instead of .then() chains"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider block">
                  Common Failures
                </label>
                <p className="text-[11px] text-subtle mb-2">Known failure modes agents should watch for and avoid</p>
                <StringListEditor
                  items={(getSpriteInstructions('commonFailures') as string[]) ?? []}
                  onChange={(v) => setSpriteInstructions('commonFailures', v)}
                  placeholder="e.g. Don't import from barrel files in test code"
                />
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10">
          <div className="bg-white border border-line rounded-xl shadow-lg px-4 py-3 flex items-center justify-between">
            <span className="text-[13px] text-muted">Unsaved changes</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setChanges({}); setSaveStatus('idle'); }}
                className="px-3 py-1.5 text-[13px] text-muted hover:text-text rounded-lg hover:bg-surface-subtle transition-colors"
              >
                <RotateCcw size={14} className="inline mr-1" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className="px-4 py-1.5 text-[13px] font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {saveStatus === 'saving' ? (
                  <span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saveStatus === 'saved' ? (
                  <Check size={14} />
                ) : (
                  <Save size={14} />
                )}
                {saveStatus === 'saved' ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
